/**
 * AnnotationPanel Component
 * Allows users to create, view, and manage track annotations
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Autocomplete,
  ToggleButton,
  ToggleButtonGroup,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
  Collapse,
  Switch,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Check as PassIcon,
  Close as FailIcon,
  Schedule as PendingIcon,
  RateReview as ReviewIcon,
  Draw as DrawIcon,
  FilterList as FilterIcon,
  MyLocation as JumpIcon,
  PlayArrow as PlayIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import * as annotationsApi from '@signals/app-shell/services/annotationsApi';
import { Annotation, AnnotationCreate, ChoiceLists } from '@signals/app-shell/services/annotationsApi';
import { useAuth } from '@signals/app-shell/hooks/useAuth';

interface MapBounds {
  west: number;
  south: number;
  east: number;
  north: number;
}

interface AnnotationPanelProps {
  courseId?: string;
  selectedStudents: string[];
  dateRange: { start: Date | null; end: Date | null };
  isDrawingAnnotation: boolean;
  onDrawModeChange: (drawing: boolean) => void;
  drawnGeometry?: any;  // GeoJSON from map
  onClearDrawing: () => void;
  onJumpToAnnotation?: (annotation: Annotation) => void;
  onLoadAnnotationData?: (annotation: Annotation) => void;  // Load track data for this annotation
  onToggleAnnotation?: (annotation: Annotation) => void;  // Toggle annotation filter on/off
  activeAnnotationIds?: number[];  // IDs of currently active annotations
  dataPoints?: any[];  // Current loaded data points for time inference
  filterByMapExtent?: boolean;  // Crop the list to annotations inside the current map view
  mapBounds?: MapBounds | null;  // Current map viewport bounds, in [west, south, east, north]
}

const STATUS_ICONS: Record<string, React.ReactNode> = {
  pass: <PassIcon sx={{ color: '#4caf50' }} />,
  fail: <FailIcon sx={{ color: '#f44336' }} />,
  pending: <PendingIcon sx={{ color: '#ff9800' }} />,
  review: <ReviewIcon sx={{ color: '#2196f3' }} />,
};

const AnnotationPanel: React.FC<AnnotationPanelProps> = ({
  courseId,
  selectedStudents,
  dateRange,
  isDrawingAnnotation,
  onDrawModeChange,
  drawnGeometry,
  onClearDrawing,
  onJumpToAnnotation,
  onLoadAnnotationData,
  onToggleAnnotation,
  activeAnnotationIds = [] as number[],
  dataPoints = [],
  filterByMapExtent = false,
  mapBounds = null,
}) => {
  const theme = useTheme();
  const { user } = useAuth();
  
  // State
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [choiceLists, setChoiceLists] = useState<ChoiceLists | null>(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAnnotation, setEditingAnnotation] = useState<Annotation | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<Partial<AnnotationCreate>>({
    custom_tags: [],
    status: 'pending',
  });
  const [tagInput, setTagInput] = useState('');
  
  // Filter state
  const [showFilters, setShowFilters] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [filterTag, setFilterTag] = useState<string>('');
  const [showMyAnnotations, setShowMyAnnotations] = useState(true); // Default to showing only current user's annotations

  // Load choice lists on mount
  useEffect(() => {
    const loadChoices = async () => {
      try {
        const choices = await annotationsApi.getChoiceLists();
        setChoiceLists(choices);
      } catch (err) {
        console.error('Failed to load annotation choices:', err);
      }
    };
    loadChoices();
  }, []);

  const loadAnnotations = useCallback(async () => {
    if (!courseId) return;
    
    setLoading(true);
    try {
      const params: any = { course_id: courseId };
      if (filterStatus) params.status = filterStatus;
      if (filterTag) params.tag = filterTag;
      if (showMyAnnotations && user) {
        // Filter by current user's email or name
        params.created_by = user.email || user.name;
      }
      
      const response = await annotationsApi.listAnnotations(params);
      setAnnotations(response.items);
    } catch (err: any) {
      setError(err.message || 'Failed to load annotations');
    } finally {
      setLoading(false);
    }
  }, [courseId, filterStatus, filterTag, showMyAnnotations, user]);

  // Load annotations when course or filters change
  useEffect(() => {
    if (courseId) {
      loadAnnotations();
    }
  }, [courseId, loadAnnotations]);

  // Crop the displayed list to annotations whose center (or any vertex of
  // their stored geometry) falls inside the current map viewport. The map
  // itself is unaffected — this only narrows down what the panel renders.
  const isInBounds = useCallback((lat?: number | null, lon?: number | null) => {
    if (!mapBounds) return true;
    if (lat == null || lon == null || !Number.isFinite(lat) || !Number.isFinite(lon)) return false;
    const { west, south, east, north } = mapBounds;
    if (lat < south || lat > north) return false;
    return west <= east ? lon >= west && lon <= east : lon >= west || lon <= east;
  }, [mapBounds]);

  const visibleAnnotations = useMemo(() => {
    if (!filterByMapExtent || !mapBounds) return annotations;
    return annotations.filter(a => {
      if (a.center_latitude != null && a.center_longitude != null) {
        if (isInBounds(a.center_latitude, a.center_longitude)) return true;
      }
      const ring = a.geometry?.coordinates?.[0];
      if (Array.isArray(ring)) {
        for (const v of ring) {
          if (Array.isArray(v) && v.length >= 2 && isInBounds(v[1], v[0])) return true;
        }
      }
      return false;
    });
  }, [annotations, filterByMapExtent, mapBounds, isInBounds]);
  const hiddenByExtentCount = annotations.length - visibleAnnotations.length;

  // When geometry is drawn, open the dialog
  // Using a ref to track processed geometry to avoid re-processing
  const processedGeometryRef = React.useRef<any>(null);
  
  useEffect(() => {
    // Only process if we have new geometry that hasn't been processed yet
    if (!drawnGeometry || drawnGeometry === processedGeometryRef.current) {
      return;
    }
    
    // Mark this geometry as processed
    processedGeometryRef.current = drawnGeometry;
    
    // Calculate center from geometry
    let centerLat = 0, centerLng = 0;
    if (drawnGeometry.type === 'Polygon' && drawnGeometry.coordinates?.[0]) {
      const coords = drawnGeometry.coordinates[0];
      coords.forEach((coord: number[]) => {
        centerLng += coord[0];
        centerLat += coord[1];
      });
      centerLat /= coords.length;
      centerLng /= coords.length;
    }

    // Infer time range from data points within the drawn area
    let startTime = dateRange.start || new Date();
    let endTime = dateRange.end || new Date();
    
    // If we have data points, find the time range of points in the area
    if (dataPoints.length > 0) {
      // Simple bounding box check for now - use forEach to avoid stack overflow on large datasets
      let minTs = Infinity, maxTs = -Infinity;
      dataPoints.forEach((p: any) => {
        if (p.coordinates && p.timestamp) {
          const ts = p.timestamp.getTime();
          if (ts < minTs) minTs = ts;
          if (ts > maxTs) maxTs = ts;
        }
      });
      
      if (minTs !== Infinity) {
        startTime = new Date(minTs);
        endTime = new Date(maxTs);
      }
    }

    setFormData({
      course_id: courseId,
      entity_id: selectedStudents[0] || '',
      geometry: drawnGeometry,
      center_latitude: centerLat,
      center_longitude: centerLng,
      start_time: startTime.toISOString(),
      end_time: endTime.toISOString(),
      custom_tags: [],
      status: 'pending',
    });
    
    setDialogOpen(true);
  }, [drawnGeometry, courseId, selectedStudents, dateRange, dataPoints]);

  const handleSave = async () => {
    if (!formData.course_id || !formData.entity_id) {
      setError('Course and Entity are required');
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      if (editingAnnotation) {
        await annotationsApi.updateAnnotation(editingAnnotation.id, {
          route_component_type: formData.route_component_type,
          custom_tags: formData.custom_tags,
          status: formData.status,
          notes: formData.notes,
        });
        setSuccess('Annotation updated successfully');
      } else {
        await annotationsApi.createAnnotation(formData as AnnotationCreate);
        setSuccess('Annotation created successfully');
      }
      
      setDialogOpen(false);
      setEditingAnnotation(null);
      setFormData({ custom_tags: [], status: 'pending' });
      onClearDrawing();
      loadAnnotations();
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to save annotation');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (annotation: Annotation) => {
    setEditingAnnotation(annotation);
    setFormData({
      course_id: annotation.course_id,
      entity_id: annotation.entity_id,
      device_type: annotation.device_type,
      start_time: annotation.start_time,
      end_time: annotation.end_time,
      geometry: annotation.geometry,
      center_latitude: annotation.center_latitude,
      center_longitude: annotation.center_longitude,
      route_component_type: annotation.route_component_type,
      custom_tags: annotation.custom_tags || [],
      status: annotation.status,
      notes: annotation.notes,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this annotation?')) return;
    
    try {
      await annotationsApi.deleteAnnotation(id);
      setSuccess('Annotation deleted');
      loadAnnotations();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to delete annotation');
    }
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.custom_tags?.includes(tagInput.trim())) {
      setFormData(prev => ({
        ...prev,
        custom_tags: [...(prev.custom_tags || []), tagInput.trim()]
      }));
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag: string) => {
    setFormData(prev => ({
      ...prev,
      custom_tags: prev.custom_tags?.filter(t => t !== tag) || []
    }));
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <Box sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontSize: '1rem', mb: user && !user.roles.includes('student') ? 0.5 : 0 }}>
              Track Annotations
            </Typography>
            {/* Creator Filter Toggle - Only show for analysts and admins */}
            {user && !user.roles.includes('student') && (
              <ToggleButtonGroup
                value={showMyAnnotations ? 'mine' : 'all'}
                exclusive
                onChange={(_, val) => val !== null && setShowMyAnnotations(val === 'mine')}
                size="small"
                sx={{ height: 28 }}
              >
                <ToggleButton value="all" sx={{ px: 1, py: 0, fontSize: '0.7rem', minWidth: 50 }}>
                  <Tooltip title="Show all annotations">
                    <span>All</span>
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="mine" sx={{ px: 1, py: 0, fontSize: '0.7rem', minWidth: 80 }}>
                  <Tooltip title="Show only my annotations">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonIcon sx={{ fontSize: 14 }} />
                      <span>My Annotations</span>
                    </Box>
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Tooltip title="Toggle filters">
              <IconButton size="small" onClick={() => setShowFilters(!showFilters)}>
                <FilterIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant={isDrawingAnnotation ? 'contained' : 'outlined'}
              size="small"
              startIcon={<DrawIcon />}
              onClick={() => onDrawModeChange(!isDrawingAnnotation)}
              color={isDrawingAnnotation ? 'secondary' : 'primary'}
            >
              {isDrawingAnnotation ? 'Cancel Draw' : 'Draw Annotation'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Drawing instruction */}
      {isDrawingAnnotation && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <strong>Click</strong> on the map to add vertices (at least 3). <strong>Double-click</strong> to finish and open the annotation form. The first vertex shows as green.
        </Alert>
      )}

      {/* Active Annotation Indicator */}
      {activeAnnotationIds.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1,
          mb: 2, 
          p: 1.5, 
          bgcolor: alpha(theme.palette.info.main, 0.1),
          border: `1px solid ${alpha(theme.palette.info.main, 0.3)}`,
          borderRadius: 1 
        }}>
          <FilterIcon sx={{ fontSize: 18, color: 'info.main' }} />
          <Typography variant="body2" color="info.main">
            Filtering by {activeAnnotationIds.length} active annotation{activeAnnotationIds.length > 1 ? 's' : ''}
          </Typography>
        </Box>
      )}

      {/* Alerts */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Filters */}
      <Collapse in={showFilters}>
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Status"
              >
                <MenuItem value="">All</MenuItem>
                {annotationsApi.ANNOTATION_STATUSES.map(status => (
                  <MenuItem key={status} value={status}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {STATUS_ICONS[status]}
                      {status.charAt(0).toUpperCase() + status.slice(1)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <InputLabel>Tag</InputLabel>
              <Select
                value={filterTag}
                onChange={(e) => setFilterTag(e.target.value)}
                label="Tag"
              >
                <MenuItem value="">All</MenuItem>
                {choiceLists?.existing_tags.map(tag => (
                  <MenuItem key={tag} value={tag}>{tag}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Paper>
      </Collapse>

      {/* Annotations List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : annotations.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
            {courseId ? 'No annotations found. Draw on the map to create one.' : 'Select a course to view annotations.'}
          </Typography>
        ) : visibleAnnotations.length === 0 ? (
          <Typography color="text.secondary" sx={{ textAlign: 'center', p: 3 }}>
            {filterByMapExtent
              ? `All ${annotations.length} annotations are outside the current map view. Pan/zoom out, or turn off "Crop to map view".`
              : 'No annotations match the current filters.'}
          </Typography>
        ) : (
          <List dense>
            {filterByMapExtent && hiddenByExtentCount > 0 && (
              <Box sx={{ px: 1, py: 0.5, mb: 1 }}>
                <Chip
                  size="small"
                  label={`Cropped to map · ${hiddenByExtentCount} hidden`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}
            {visibleAnnotations.map((annotation) => {
              const isActive = activeAnnotationIds.includes(annotation.id);
              return (
                <Paper 
                  key={annotation.id} 
                  sx={{ 
                    mb: 1,
                    border: isActive ? '2px solid' : '1px solid',
                    borderColor: isActive ? 'primary.main' : 'divider',
                    bgcolor: isActive ? 'action.selected' : 'background.paper'
                  }}
                >
                  <ListItem>
                    {/* Toggle Switch */}
                    {onToggleAnnotation && (
                      <Tooltip title={isActive ? "Filter active - click to turn off" : "Click to filter by this annotation"}>
                        <Switch
                          size="small"
                          checked={isActive}
                          onChange={() => onToggleAnnotation(annotation)}
                          color="primary"
                          sx={{ mr: 1 }}
                        />
                      </Tooltip>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                      {STATUS_ICONS[annotation.status]}
                    </Box>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Typography variant="body2" fontWeight="bold">
                            {annotation.entity_id}
                          </Typography>
                          {annotation.route_component_type && (
                            <Chip
                              label={annotation.route_component_type}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                          {annotation.custom_tags?.map(tag => (
                            <Chip key={tag} label={tag} size="small" />
                          ))}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" display="block">
                            {formatDate(annotation.start_time)} - {formatDate(annotation.end_time)}
                          </Typography>
                          {annotation.created_by && (
                            <Typography variant="caption" display="block" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'primary.main', mt: 0.25 }}>
                              <PersonIcon sx={{ fontSize: 12 }} />
                              {annotation.created_by}
                            </Typography>
                          )}
                          {annotation.notes && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {annotation.notes.substring(0, 100)}{annotation.notes.length > 100 ? '...' : ''}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      {onLoadAnnotationData && (
                        <Tooltip title="Load data & activate filter">
                          <IconButton 
                            size="small" 
                            onClick={() => onLoadAnnotationData(annotation)}
                            color={isActive ? "primary" : "default"}
                          >
                            <PlayIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      {onJumpToAnnotation && annotation.center_latitude && (
                        <Tooltip title="Jump to location">
                          <IconButton size="small" onClick={() => onJumpToAnnotation(annotation)}>
                            <JumpIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(annotation)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => handleDelete(annotation.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  </ListItem>
                </Paper>
              );
            })}
          </List>
        )}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setEditingAnnotation(null); onClearDrawing(); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingAnnotation ? 'Edit Annotation' : 'Create Annotation'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Entity Selection */}
            <FormControl fullWidth size="small">
              <InputLabel>Entity</InputLabel>
              <Select
                value={formData.entity_id || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, entity_id: e.target.value }))}
                label="Entity"
              >
                {selectedStudents.map(student => (
                  <MenuItem key={student} value={student}>{student}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Route Component Type */}
            <FormControl fullWidth size="small">
              <InputLabel>Route Component</InputLabel>
              <Select
                value={formData.route_component_type || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, route_component_type: e.target.value }))}
                label="Route Component"
              >
                <MenuItem value="">None</MenuItem>
                {annotationsApi.ROUTE_COMPONENT_TYPES.map(type => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status */}
            <Box>
              <Typography variant="body2" gutterBottom>Status</Typography>
              <ToggleButtonGroup
                value={formData.status}
                exclusive
                onChange={(_, value) => value && setFormData(prev => ({ ...prev, status: value }))}
                size="small"
                fullWidth
              >
                <ToggleButton value="pass" sx={{ color: formData.status === 'pass' ? '#4caf50' : undefined }}>
                  <PassIcon sx={{ mr: 0.5 }} /> Pass
                </ToggleButton>
                <ToggleButton value="fail" sx={{ color: formData.status === 'fail' ? '#f44336' : undefined }}>
                  <FailIcon sx={{ mr: 0.5 }} /> Fail
                </ToggleButton>
                <ToggleButton value="pending" sx={{ color: formData.status === 'pending' ? '#ff9800' : undefined }}>
                  <PendingIcon sx={{ mr: 0.5 }} /> Pending
                </ToggleButton>
                <ToggleButton value="review" sx={{ color: formData.status === 'review' ? '#2196f3' : undefined }}>
                  <ReviewIcon sx={{ mr: 0.5 }} /> Review
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>

            {/* Tags */}
            <Box>
              <Typography variant="body2" gutterBottom>Tags</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                <Autocomplete
                  freeSolo
                  size="small"
                  options={choiceLists?.existing_tags || []}
                  inputValue={tagInput}
                  onInputChange={(_, value) => setTagInput(value)}
                  onChange={(_, value) => {
                    if (value && !formData.custom_tags?.includes(value)) {
                      setFormData(prev => ({
                        ...prev,
                        custom_tags: [...(prev.custom_tags || []), value]
                      }));
                    }
                    setTagInput('');
                  }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder="Add tag..." sx={{ flex: 1 }} />
                  )}
                  sx={{ flex: 1 }}
                />
                <Button variant="outlined" onClick={handleAddTag} disabled={!tagInput.trim()}>
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                {formData.custom_tags?.map(tag => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    onDelete={() => handleRemoveTag(tag)}
                  />
                ))}
              </Box>
            </Box>

            {/* Notes */}
            <TextField
              label="Notes"
              multiline
              rows={3}
              value={formData.notes || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
              fullWidth
            />

            {/* Time Range (read-only for now) */}
            {formData.start_time && (
              <Box sx={{ bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Time Range: {formatDate(formData.start_time)} - {formatDate(formData.end_time || '')}
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDialogOpen(false); setEditingAnnotation(null); onClearDrawing(); }}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : (editingAnnotation ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AnnotationPanel;
